import { useState } from "react";
import { Pencil, Trash2, Tag, Handshake, Coffee, Star, DollarSign, Building2, Mail, Phone, FileText, CalendarCheck, Calendar } from "lucide-react";
import { describeEvent, getEventColor, getWeekKey } from "@/lib/chartUtils";
import { updateEvent, deleteEvent } from "@/lib/api";
import EditDialog from "./EditDialog";

const ICONS = { tag: Tag, referral: Handshake, oneOnOne: Coffee, guest: Star, biz: DollarSign };
const LABELS = { tag: "Tag", referral: "Referral", oneOnOne: "1-on-1", guest: "Guest", biz: "Closed $" };

export default function EventLog({ events, members: rawMembers, onDataChanged, onEditingChange }) {
  const members = [...rawMembers].sort((a, b) => a.localeCompare(b));
  const [editingEvent, setEditingEvent] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const wk = getWeekKey(new Date());

  function startEditing(event) {
    setEditingEvent({ ...event });
    if (onEditingChange) onEditingChange(true);
  }

  function stopEditing() {
    setEditingEvent(null);
    if (onEditingChange) onEditingChange(false);
  }

  async function handleSave(updated) {
    try {
      const { id, weekKey, monthKey, ts, seeded, ...rest } = updated;
      // Strip fields that would fail validation (e.g. amt="" for non-biz types)
      const cleaned = {};
      for (const [k, v] of Object.entries(rest)) {
        if (v === undefined || v === null) continue;
        if (k === "amt" && (v === "" || v === 0)) continue;
        cleaned[k] = v;
      }
      await updateEvent(id, cleaned);
      stopEditing();
      onDataChanged(true);
    } catch (err) {
      console.error("Save failed:", err);
    }
  }

  async function handleDelete(id) {
    try {
      setDeletingId(id);
      await deleteEvent(id);
      onDataChanged();
    } catch {
      // handled silently
    } finally {
      setDeletingId(null);
    }
  }

  async function toggleVisitStatus(e) {
    const newStatus = e.visitStatus === "visited" ? "scheduled" : "visited";
    try {
      await updateEvent(e.id, { visitStatus: newStatus });
      onDataChanged(true);
    } catch { /* */ }
  }

  const sorted = [...events].sort((a, b) => new Date(b.ts) - new Date(a.ts));

  return (
    <div data-testid="event-log">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase">
          All Events ({events.length})
        </h3>
        <span className="text-[10px] text-zinc-400">Click pencil to edit</span>
      </div>

      {events.length === 0 && (
        <p className="text-center text-zinc-400 py-10 text-sm">No events yet. Log your first activity above!</p>
      )}

      <div className="space-y-1">
        {sorted.map(e => {
          const c = getEventColor(e.type);
          const Icon = ICONS[e.type] || Tag;
          const isCurrentWeek = e.weekKey === wk;

          if (editingEvent?.id === e.id) {
            return (
              <EditDialog
                key={e.id}
                event={editingEvent}
                members={members}
                onSave={handleSave}
                onCancel={stopEditing}
              />
            );
          }

          return (
            <div
              key={e.id}
              data-testid={`event-row-${e.id}`}
              className={`flex items-start gap-3 px-3 py-2.5 rounded-md border-l-[3px] transition-colors hover:bg-zinc-50 ${
                e.seeded && !e.editedAt ? 'border-l-zinc-300' : isCurrentWeek ? 'border-l-green-500' : 'border-l-zinc-200'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold ${c.bg} ${c.text}`}>
                    <Icon className="w-3 h-3" />
                    {LABELS[e.type]}
                  </span>
                  <span className="text-sm text-zinc-700 font-body">{describeEvent(e)}</span>
                  {e.editedAt && (
                    <span className="text-[10px] font-semibold bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">edited</span>
                  )}
                  {e.seeded && !e.editedAt && (
                    <span className="text-[10px] font-semibold bg-zinc-100 text-zinc-400 px-1.5 py-0.5 rounded">seed</span>
                  )}
                </div>
                {/* Referral notes */}
                {e.type === "referral" && e.notes && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded px-2 py-1">
                    <FileText className="w-3 h-3" /> {e.notes}
                  </div>
                )}
                {/* Guest contact info */}
                {e.type === "guest" && (e.guestPhone || e.guestEmail || e.guestBiz) && (
                  <div className="mt-1.5 flex gap-2 flex-wrap">
                    {e.guestBiz && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-700 bg-green-50 border border-green-100 rounded px-2 py-0.5">
                        <Building2 className="w-3 h-3" /> {e.guestBiz}
                      </span>
                    )}
                    {e.guestEmail && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-700 bg-green-50 border border-green-100 rounded px-2 py-0.5">
                        <Mail className="w-3 h-3" /> {e.guestEmail}
                      </span>
                    )}
                    {e.guestPhone && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-700 bg-green-50 border border-green-100 rounded px-2 py-0.5">
                        <Phone className="w-3 h-3" /> {e.guestPhone}
                      </span>
                    )}
                  </div>
                )}
                {/* Visit status toggle */}
                {e.type === "guest" && e.visitStatus && (
                  <button
                    onClick={() => toggleVisitStatus(e)}
                    className={`mt-1.5 inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold rounded-md transition-colors ${
                      e.visitStatus === "visited"
                        ? "bg-green-100 text-green-700 border border-green-200 hover:bg-green-200"
                        : "bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200"
                    }`}
                  >
                    {e.visitStatus === "visited" ? (
                      <><CalendarCheck className="w-3.5 h-3.5" /> Visited</>
                    ) : (
                      <><Calendar className="w-3.5 h-3.5" /> Scheduled — tap to mark visited</>
                    )}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[11px] text-zinc-400 whitespace-nowrap">
                  {new Date(e.ts).toLocaleDateString([], { month: "short", day: "numeric" })}{" "}
                  {new Date(e.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
                <button
                  data-testid={`edit-event-${e.id}`}
                  onClick={() => startEditing(e)}
                  className="p-1.5 rounded-md border border-zinc-200 hover:bg-zinc-100 transition-colors"
                  title="Edit"
                >
                  <Pencil className="w-3.5 h-3.5 text-zinc-500" />
                </button>
                <button
                  data-testid={`delete-event-${e.id}`}
                  onClick={() => handleDelete(e.id)}
                  disabled={deletingId === e.id}
                  className="p-1.5 rounded-md border border-zinc-200 hover:bg-red-50 hover:border-red-200 transition-colors disabled:opacity-40"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5 text-zinc-400 hover:text-red-500" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
